import { StandaloneAddressQuickSearchForm, YesNoRadioButtonGroupRaw, useWithAddressVerify } from '@uplift/ui-v2';
import { PROFILE_QUERY_KEY } from '../profile.utils';
import { StepFormikContainer } from './StepFormContainer.component';
import { useProfileContext } from '../profile.context';
import { useMemo, useState } from 'react';
import { AddressQuickSearchFormType, AddressSessionOptionFormType } from '../forms/types';
import { PracticeLocation, savePracticeLocation } from '@uplift/api';
import { FormikConfig } from 'formik';
import { ServiceTypeForm } from '../forms';
import { practiceAddressValidationSchema } from '../forms/validation';
import { useQueryClient } from '@tanstack/react-query';
import { useL10n } from '@uplift/hooks';
import { SeeClientsEnum } from '@uplift/types';
import { LocalStorageService } from '@uplift/services';

export type PracticeAddressStepProps = {
  onNext: () => void;
  onBack: () => void;
  practiceAddressEditIndex: number;
  setPracticeAddressEditIndex: (index: number) => void;
};

const PRACTICE_ADDRESS_ADD_ANOTHER_LOCAL_STORAGE_KEY = 'practice_address_add_another';

export const PracticeAddressStep: React.VFC<PracticeAddressStepProps> = props => {
  const isAddressStep = props.practiceAddressEditIndex % 2 === 0;
  const locationIndex = Math.floor(props.practiceAddressEditIndex / 2);
  const { profile } = useProfileContext();
  const location = profile.locations[locationIndex];
  const [ addAnotherLocation, setAddAnotherLocation ] = useState(() => {

    if (locationIndex === 0 || !isAddressStep || !!location) {
      return true;
    }

    return LocalStorageService.getItem<boolean>(PRACTICE_ADDRESS_ADD_ANOTHER_LOCAL_STORAGE_KEY);
  });

  const onSetAddAnotherLocation = (value: boolean) => {
    setAddAnotherLocation(value);
    LocalStorageService.setItem(PRACTICE_ADDRESS_ADD_ANOTHER_LOCAL_STORAGE_KEY, value);
  };

  const onNext = () => {
    if (addAnotherLocation || props.practiceAddressEditIndex === 0) {
      props.setPracticeAddressEditIndex(props.practiceAddressEditIndex + 1);
      return;
    }

    props.onNext();
  };

  const onBack = () => {
    if (props.practiceAddressEditIndex === 0) {
      props.onBack();
      return;
    }

    props.setPracticeAddressEditIndex(props.practiceAddressEditIndex - 1);
  };

  if (isAddressStep) {
    return (
      <AddressFormContainer
        onSkip={props.onNext}
        onNext={onNext}
        onBack={onBack}
        location={location}
        isFirst={locationIndex === 0}
        addAnotherLocation={addAnotherLocation}
        setAddAnotherLocation={onSetAddAnotherLocation}
      />
    );
  }


  return <ServiceTypeFormContainer onNext={onNext} onBack={onBack} location={location} />;
};

type AddressFormContainerProps = {
  location: PracticeLocation | undefined;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  isFirst: boolean;
  addAnotherLocation: boolean;
  setAddAnotherLocation: (value: boolean) => void;
};

const AddressFormContainer: React.VFC<AddressFormContainerProps> = ({ onNext, onBack, onSkip, location, isFirst, addAnotherLocation, setAddAnotherLocation }) => {
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const withAddressVerify = useWithAddressVerify();

  const initialValues = useMemo((): AddressQuickSearchFormType => {
    const address = location?.address;

    return {
      predefined: false,
      address1: address?.line1 ?? '',
      address2: address?.line2 ?? '',
      city: address?.city ?? '',
      state: address?.stateId ?? '',
      zip: address?.zip ?? '',
      search: '',
    };
  }, [ location?.address ]);

  const onSubmit: FormikConfig<AddressQuickSearchFormType>['onSubmit'] = withAddressVerify(async (values) => {
    await savePracticeLocation({
      replaceLocationId: location?.id,

      streetAddressHidden: true,
      address: {
        line1: values.address1,
        line2: values.address2 ?? null,
        city: values.city,
        stateId: values.state,
        zip: values.zip,
      },

      inPersonVisits: location?.inPersonVisits ?? false,
      phone: location?.phone,
      phoneExt: location?.phoneExt,
      practiceServiceType: location?.practiceServiceType ?? SeeClientsEnum.telehealth,
      isHandicapAccessible: location?.isHandicapAccessible ?? null,
    });

    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY(profile.user.id) });
    onNext();
  }, {
    initialAddress: initialValues,
    shouldCheck: true,
  });

  if (isFirst) {
    return (
      <StepFormikContainer
        title="Where do you conduct your practice?"
        description="We currently don't display full addresses in your public profile: We use your address for tax purposes and to confirm where you're holding sessions."
        form={<StandaloneAddressQuickSearchForm />}
        formikProps={{
          initialValues,
          onSubmit,
          validationSchema: practiceAddressValidationSchema(l10n)
        }}
        onBack={onBack}
        onSkip={onSkip}
        isSkippable={formik => !formik.dirty && !location}
        requireValidation
      />
    );
  }

  return (
    <StepFormikContainer
      title="Do you have another practice address you'd like to use?"
      form={addAnotherLocation ? <StandaloneAddressQuickSearchForm /> : null}
      nestForm
      formikProps={{
        initialValues,
        onSubmit: onSubmit,
        validationSchema: practiceAddressValidationSchema(l10n),
      }}
      onBack={onBack}
      onSkip={onNext}
      isSkippable={() => !addAnotherLocation}
      requireValidation
    >
      <YesNoRadioButtonGroupRaw
        asBoolean
        name="addAnotherLocation"
        onClick={(addAnotherLocation: boolean) => setAddAnotherLocation(addAnotherLocation)}
        value={addAnotherLocation}
        disabled={location?.id !== undefined}
      />
    </StepFormikContainer>
  );
};

type ServiceTypeFormContainerProps = {
  location: PracticeLocation | undefined;
  onNext: () => void;
  onBack: () => void;
};

const ServiceTypeFormContainer: React.VFC<ServiceTypeFormContainerProps> = ({ onNext, onBack, location }) => {
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();


  const initialValues: AddressSessionOptionFormType = {
    seeClientsType: location?.practiceServiceType ?? SeeClientsEnum.telehealth,
  };

  const onSubmit: FormikConfig<AddressSessionOptionFormType>['onSubmit'] = async (values) => {
    await savePracticeLocation({
      replaceLocationId: location?.id,
      practiceServiceType: values.seeClientsType,

      address: location?.address,
      inPersonVisits: location?.inPersonVisits,
      streetAddressHidden: location?.streetAddressHidden,
      phone: location?.phone,
      phoneExt: location?.phoneExt,
      isHandicapAccessible: location?.isHandicapAccessible,
    });

    await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY(profile.user.id) });
    onNext();
  };

  return (
    <StepFormikContainer
      title={`What type of sessions do you want to hold at ${location?.address?.line1 ?? 'this location'}?`}
      form={<ServiceTypeForm />}
      formikProps={{
        initialValues,
        onSubmit,
      }}
      onBack={onBack}
      onSkip={onNext}
    />
  );
};
